import {objectToCamelCase, objectToSnakeCase} from '../utils'
import api from './api'
import SurveyQuestion from '@/services/surveyQuestions'
import Survey from '@/services/surveys'

export default class SurveyResponse {
  static api = api.create(SurveyResponse)
  static readOnlyFields = ['id', 'surveyRef', 'datetimeCreated', 'lastEdited']
  constructor(surveyResponse) {
    const {
      id = '',
      survey = null,
      surveyRef = null,
      datetimeCreated = null,
      lastEdited = null,
      questions = [],
      isComplete = null,
      allowPartialResponses = null,
      instructions = '',
      instructionsEs = '',
      instructionsEn = '',
    } = surveyResponse
    Object.assign(this, {
      id,
      survey,
      surveyRef,
      datetimeCreated,
      lastEdited,
      questions: questions ? questions.map(SurveyQuestion.fromAPI) : null,
      isComplete,
      allowPartialResponses,
      instructions,
      instructionsEs,
      instructionsEn,
    })
  }

  static create(opts) {
    return new SurveyResponse(opts)
  }

  static fromAPI(json) {
    return new SurveyResponse(objectToCamelCase(json))
  }

  /**
   * Prep data for the API. Can elect to include or exlude specific fields.
   * The readOnlyFields are always excluded.
   **/
  static toAPI(surveyResponse, fields = [], excludeFields = []) {
    // By default, send the whole object
    let data = {}

    // If it's a partial update, get only the fields specified
    if (fields.length > 0) {
      fields.forEach(field => {
        data[field] = surveyResponse[field]
      })
    } else {
      data = surveyResponse
    }

    //
    excludeFields = [...SurveyResponse.readOnlyFields, ...excludeFields]
    excludeFields.forEach(item => {
      delete data[item]
    })

    return objectToSnakeCase(data)
  }
  static checkIsInstance(obj, name) {
    if (!(obj instanceof SurveyResponse)) {
      throw Error(`${name} must be an instance of the SurveyResponse class.`)
    }
  }

  /* Return true if an employee has completed a survey response for a particular survey. */
  static async checkIfEmployeeHasCompleted(employee, surveyName) {
    if (!Survey.options.SURVEY_NAMES.includes(surveyName)) {
      throw Error(`Invalid survey name ${surveyName} provided.`)
    }
    const response = await SurveyResponse.api.list({
      filters: {employee: employee.id, surveyName},
    })
    const surveyResponses = response.results
    // If no survey responses exist for this survey, return false
    if (surveyResponses.length == 0) {
      return false
    }
    // Loop through all survey responses. If at least one is complete, return true
    for (const sr of surveyResponses) {
      if (sr.isComplete) {
        return true
      }
    }
    return false
  }

  /* Create or retrieve an employee's survey response for a particular survey */
  static async getOrCreate(employee, surveyName) {
    if (!Survey.options.SURVEY_NAMES.includes(surveyName)) {
      throw Error(`Invalid survey name ${surveyName} provided.`)
    }
    const response = await SurveyResponse.api.list({
      filters: {employee: employee.id, surveyName},
    })
    const surveyResponses = response.results

    // If employee has 1+ responses, return the first response
    if (surveyResponses.length !== 0) {
      const surveyResponse = surveyResponses[0]
      // Perform a retrieve, since the Retrieve endpoint returns more details than List
      const fullSurveyResponse = await SurveyResponse.api.retrieve(surveyResponse.id)
      return fullSurveyResponse

      // If employee has no responses, create a new one
    } else {
      // Pull the first survey with this name (there should only be one)
      const response = await Survey.api.list({
        filters: {name_en: surveyName},
      })
      const surveyId = response.results[0].id
      // Create a SurveyResponse and return it
      const surveyResponse = await SurveyResponse.api.create(
        SurveyResponse.create({
          survey: surveyId,
          questions: [],
        }),
      )
      return surveyResponse
    }
  }
}
